import { computed, ref, toValue } from 'vue';

import { UseSelection, UseSelectionArgs } from './useSelection.types';

export default function useSelection<Item extends object>({
  items,
  trackBy = 'id',
  shouldDisable,
  shouldPreselect,
}: UseSelectionArgs<Item>): UseSelection<Item> {
  const itemsRef = computed(() => toValue(items));

  function getItemKey(item: Item) {
    return String(item[trackBy]);
  }

  const itemsByKey = computed(() =>
    itemsRef.value.reduce(
      (map, item) => {
        map[getItemKey(item)] = item;

        return map;
      },
      {} as { [key: string]: Item },
    ),
  );

  /**
   * A set of item keys that are currently selected, regardless if they are disabled
   */
  const selectedSet = ref(new Set<string>());

  function select(item: Item) {
    if (shouldDisable?.(itemsByKey.value[getItemKey(item)])) {
      return;
    }

    selectedSet.value.add(getItemKey(item));
  }

  function unselect(item: Item) {
    if (shouldDisable?.(itemsByKey.value[getItemKey(item)])) {
      return;
    }

    selectedSet.value.delete(getItemKey(item));
  }

  function selectToggle(item: Item) {
    if (shouldDisable?.(itemsByKey.value[getItemKey(item)])) {
      return;
    }

    const isSelected = selectedSet.value.has(getItemKey(item));

    if (isSelected) {
      selectedSet.value.delete(getItemKey(item));
    } else {
      selectedSet.value.add(getItemKey(item));
    }
  }

  function selectAll() {
    itemsRef.value.forEach((item) => {
      if (!shouldDisable?.(item)) {
        selectedSet.value.add(getItemKey(item));
      }
    });
  }

  function unselectAll() {
    if (typeof shouldDisable === 'function') {
      selectedSet.value.forEach((_isSelected, itemKey) => {
        if (!shouldDisable(itemsByKey.value[itemKey])) {
          selectedSet.value.delete(itemKey);
        }
      });
    } else {
      selectedSet.value.clear();
    }
  }

  function selectToggleAll() {
    if (allNonDisabledSelected.value) {
      unselectAll();
    } else {
      selectAll();
    }
  }

  function resetSelection() {
    unselectAll();

    if (typeof shouldPreselect === 'function') {
      itemsRef.value.forEach((item) => {
        if (shouldPreselect(item)) {
          selectedSet.value.add(getItemKey(item));
        }
      });
    }
  }

  function isSelected(item: Item) {
    return selectedSet.value.has(getItemKey(item));
  }

  function isSelectDisabled(item: Item) {
    if (typeof shouldDisable !== 'function') {
      return false;
    }

    return shouldDisable(itemsByKey.value[getItemKey(item)]);
  }

  const selectedItems = computed(() => {
    return Array.from(selectedSet.value.keys()).map((itemKey) => itemsByKey.value[itemKey]);
  });

  const allSelected = computed(() => {
    return selectedSet.value.size === itemsRef.value.length;
  });

  const allNonDisabledSelected = computed(() => {
    if (typeof shouldDisable !== 'function') {
      return allSelected.value;
    }

    for (const item of itemsRef.value) {
      if (!shouldDisable(item) && !selectedSet.value.has(getItemKey(item))) {
        return false;
      }
    }

    return true;
  });

  const someSelected = computed(() => {
    return selectedSet.value.size > 0;
  });

  const someNonDisabledSelected = computed(() => {
    if (typeof shouldDisable !== 'function') {
      return someSelected.value;
    }

    for (const itemKey of selectedSet.value.keys()) {
      if (!shouldDisable(itemsByKey.value[itemKey])) {
        return true;
      }
    }

    return false;
  });

  resetSelection();

  return {
    select,
    unselect,
    selectToggle,
    selectAll,
    unselectAll,
    selectToggleAll,
    resetSelection,
    isSelected,
    isSelectDisabled,
    selectedItems,
    allSelected,
    allNonDisabledSelected,
    someSelected,
    someNonDisabledSelected,
  };
}

export * from './useSelection.types';
